// ── Report generate section — SA/Admin/coach-in-scope, LIVE POST (FR-REPORT) ─
// POST /sessions/:session_id/reports/generate is NOW LIVE. generate.reports
// grants this to super_admin / admin / coach-in-scope — the section renders for
// those roles only (canGenerateReports); the backend re-applies the in-scope
// guard (a foreign session 404s-via-400). With a generated UUID Idempotency-Key
// the action enqueues generation; while the report-agent lane is closed the
// response is an HONEST pending/queued (report:null, generated:false) — it NEVER
// fabricates a generated report. The toast fires only AFTER the backend confirms.

import { Alert, Button, Module, ModuleBody, ModuleHead, useToast } from "@vjal/ui";
import { Sparkles } from "lucide-react";

import { useReportGenerateMutation } from "../../../hooks/useCoachingReportGeneration";
import { canGenerateReports } from "../reports-helpers";
import { describeReportWriteError } from "../reports-write";
import { ReportEnqueueResult } from "../report-enqueue-result";
import type { CoachingRole } from "../../../domain/roles";

export function ReportGenerateSection({ role, sessionId }: { role: CoachingRole; sessionId: string | null | undefined }) {
  const toast = useToast();
  const generate = useReportGenerateMutation();

  if (!canGenerateReports(role)) return null;

  const validSession = typeof sessionId === "string" && sessionId.trim().length > 0;
  const writeError = describeReportWriteError(generate.error);

  function run() {
    if (!validSession) return;
    generate.reset();
    generate.mutate(sessionId as string, { onSuccess: () => toast.success("Generation queued") });
  }

  return (
    <Module span={4}>
      <ModuleHead title="Generate" subtitle="Enqueue report generation for this session." />
      <ModuleBody className="space-y-3" data-testid="report-generate">
        <Button
          type="button"
          variant="secondary"
          leftIcon={<Sparkles size={16} />}
          disabled={!validSession}
          loading={generate.isPending}
          onClick={run}
          aria-label="Generate report"
        >
          Generate report
        </Button>
        <ReportEnqueueResult result={generate.data ?? null} />
        {writeError ? <Alert variant={writeError.tone}>{writeError.message}</Alert> : null}
      </ModuleBody>
    </Module>
  );
}
